import * as z from 'zod';
import { DateTime } from 'luxon';
import { useLanguage } from 'features/hooks/useLanguage';
import { LanguageType } from 'types';
import {
	CodelistHistoryEvent,
	useExportCodelistHistoryMutation
} from 'features/codelist/export/exportCodelistHistoryMutation';
import { useGetNamesFromUserIdsQuery } from 'features/entry-form-v2/data/useGetNamesFromUserIdsQuery';
import * as XLSX from 'xlsx';
import { SearchRecordsFilter } from '../../../features/codelist/data/useSearchRecordsMutation';
import { EVENT_TYPE_DICTIONARY } from './event-type-dictionary';
import { Button } from '../../../features/components/Button';
import { useProject, useUsername } from '../../../hooks/store';
import { toast } from 'react-toastify';

interface CodelistHistoryExportProps {
	projectId: string;
	userIdSet: Set<string>;
}

export const CodelistHistoryExport = ({ projectId, userIdSet }: CodelistHistoryExportProps) => {
	const language = useLanguage();
	const translation = DICTIONARY[language];

	const exportCodelistHistoryMutation = useExportCodelistHistoryMutation({
		projectId: projectId
	});

	const getNamesFromUserIdsQuery = useGetNamesFromUserIdsQuery({
		userIds: Array.from(userIdSet)
	});

	const handleExport = async () => {
		const result = await exportCodelistHistoryMutation.mutateAsync();

		const dateShort = DateTime.fromJSDate(new Date()).toLocaleString(DateTime.DATE_SHORT);
		const fileName = `${translation.filename}_${projectId}-${dateShort}`;

		downloadAsExcel({
			filename: `${fileName}.xlsx`,
			events: result.events,
			userNamesByUserId: getNamesFromUserIdsQuery.data?.namesFromUserIds ?? {},
			language
		});
	};

	const [{ data: project }] = useProject();
	const userId = useUsername();

	const isProjectOwner = project?.projectOwnerDetails?.userId === userId;

	if (!isProjectOwner) {
		return null;
	}

	return (
		<div className="flex items-center gap-4">
			<Button
				text={translation.exportHistory}
				loading={
					exportCodelistHistoryMutation.isLoading || getNamesFromUserIdsQuery.isLoading
				}
				onClick={handleExport}
			/>
		</div>
	);
};

const downloadAsExcel = (args: {
	events: CodelistHistoryEvent[];
	filename: string;
	userNamesByUserId: Record<string, string>;
	language: LanguageType;
}) => {
	const workbook = XLSX.utils.book_new();

	const importEventSheetMap = new Map<string, number>();
	const totalImportEvents = args.events.filter(
		e => e.eventType === 'IMPORT_BULK_RECORDS' || e.eventType === 'PARTIAL_IMPORT_RECORDS'
	).length;
	let importEventSheetCounter = totalImportEvents;

	args.events.forEach(event => {
		if (
			event.eventType === 'IMPORT_BULK_RECORDS' ||
			event.eventType === 'PARTIAL_IMPORT_RECORDS'
		) {
			importEventSheetMap.set(event.id, importEventSheetCounter);
			importEventSheetCounter--;
		}
	});

	const withFlattenedMetadata = args.events.map(event =>
		flattenMetadata({
			event,
			userNamesByUserId: args.userNamesByUserId,
			language: args.language,
			importEventSheetMap
		})
	);

	const historyLogSheet = XLSX.utils.json_to_sheet(withFlattenedMetadata);
	XLSX.utils.book_append_sheet(workbook, historyLogSheet, 'History Log');

	// Create separate sheets for each IMPORT_BULK_RECORDS event
	args.events.forEach(event => {
		if (
			(event.eventType === 'IMPORT_BULK_RECORDS' ||
				event.eventType === 'PARTIAL_IMPORT_RECORDS') &&
			event.metadata
		) {
			const sheetNumber = importEventSheetMap.get(event.id);
			if (!sheetNumber) {
				console.error(
					'Sheet number not found for IMPORT_BULK_RECORDS event. This should not happen.'
				);
				toast.error(DICTIONARY[args.language].genericError);
				return;
			}

			try {
				const parsedMetadata = importBulkRecordsMetadataSheet(JSON.parse(event.metadata));
				const metadataSheet = XLSX.utils.json_to_sheet(parsedMetadata.records);

				XLSX.utils.book_append_sheet(workbook, metadataSheet, `Import ${sheetNumber}`);
			} catch (error) {
				console.error('Error parsing IMPORT_BULK_RECORDS metadata:', error);
			}
		}
	});

	XLSX.writeFile(workbook, args.filename);
};

type SearchRecordsEventMetadata = {
	filters: SearchRecordsFilter[];
};

type DeleteRecordsEventMetadata = {
	recordIds: string[];
};

type CreateRecordEventMetadata = {
	record: {
		id: string;
		customId: string;
		name: string;
	};
};

type EventColumns = {
	Timestamp: string;
	User: string;
	Event: string;
	'Record ID'?: string;
	Record?: string;
	'Search Filters'?: string;
	'Metadata Sheet'?: string;
};

const flattenMetadata = (args: {
	event: CodelistHistoryEvent;
	userNamesByUserId: Record<string, string>;
	language: LanguageType;
	importEventSheetMap: Map<string, number>;
}): EventColumns => {
	const { event, userNamesByUserId, importEventSheetMap } = args;

	const userName = userNamesByUserId[event.subject];

	const eventTypeTranslation = EVENT_TYPE_DICTIONARY[args.language][event.eventType];

	if (event.eventType === 'GET_RECORD' || event.eventType === 'GET_RECORD_HISTORY') {
		return {
			Timestamp: event.createdAt,
			User: userName,
			Event: eventTypeTranslation,
			'Record ID': event.entityId
		};
	}

	if (!event.metadata) {
		return {
			Timestamp: event.createdAt,
			User: userName,
			Event: eventTypeTranslation
		};
	}

	if (event.eventType === 'IMPORT_BULK_RECORDS' || event.eventType === 'PARTIAL_IMPORT_RECORDS') {
		const sheetNumber = importEventSheetMap.get(event.id);

		try {
			const parsedEventMetadata: CreateRecordEventMetadata = JSON.parse(event.metadata);

			return {
				Timestamp: event.createdAt,
				User: userName,
				Event: eventTypeTranslation,
				'Record ID': parsedEventMetadata.record?.id,
				'Metadata Sheet':
					sheetNumber !== undefined ? `See Import ${sheetNumber}-sheet` : undefined
			};
		} catch (error) {
			console.error('Error parsing IMPORT_BULK_RECORDS metadata:', error);

			throw error;
		}
	}

	if (event.eventType === 'CREATE_RECORD' || event.eventType === 'UPDATE_RECORD') {
		const parsedEventMetadata: CreateRecordEventMetadata = JSON.parse(event.metadata);

		return {
			Timestamp: event.createdAt,
			User: userName,
			Event: eventTypeTranslation,
			'Record ID': parsedEventMetadata.record.id,
			Record: JSON.stringify(parsedEventMetadata.record)
		};
	}

	if (event.eventType === 'SEARCH_RECORDS') {
		const parsedEventMetadata: SearchRecordsEventMetadata = JSON.parse(event.metadata);

		return {
			Timestamp: event.createdAt,
			User: userName,
			Event: eventTypeTranslation,
			'Search Filters': JSON.stringify(parsedEventMetadata.filters)
		};
	}

	if (event.eventType === 'DELETE_RECORDS') {
		const parsedEventMetadata: DeleteRecordsEventMetadata = JSON.parse(event.metadata);

		return {
			Timestamp: event.createdAt,
			User: userName,
			Event: eventTypeTranslation,
			'Record ID': parsedEventMetadata.recordIds.join(', ')
		};
	}

	return {
		Timestamp: event.createdAt,
		User: userName,
		Event: eventTypeTranslation
	};
};

const DICTIONARY = {
	[LanguageType.English]: {
		createdAt: 'Timestamp',
		action: 'Action',
		user: 'User',
		metadata: 'Metadata',
		exportHistory: 'Export History',
		formatTitle: 'Export Format',
		filename: 'codelist-history-export',
		csv: 'CSV',
		xls: 'Excel',

		genericError: 'An error occurred while exporting the history.',

		...EVENT_TYPE_DICTIONARY[LanguageType.English]
	},

	[LanguageType.Norwegian]: {
		createdAt: 'Tidspunkt',
		action: 'Hendelse',
		user: 'Bruker',
		metadata: 'Metadata',
		exportHistory: 'Eksporter historikk',
		formatTitle: 'Eksportformat',
		filename: 'kodeliste-historikk-eksport',
		csv: 'CSV',
		xls: 'Excel',

		genericError: 'En feil oppsto under eksport av historikken.',

		...EVENT_TYPE_DICTIONARY[LanguageType.Norwegian]
	}
};

const importBulkRecordsMetadataSheet = (metadata: unknown) => {
	return z
		.object({
			records: z.array(
				z.object({
					id: z.string().optional(),
					customId: z.string().optional(),
					ssn: z.string().optional(),
					firstName: z.string().optional(),
					lastName: z.string().optional(),
					dateOfBirth: z.string().optional(),
					mobilePhone: z.string().optional(),
					email: z.string().optional(),
					address: z.string().optional(),
					postalCode: z.string().optional(),
					postalPlace: z.string().optional(),
					country: z.string().optional()
				})
			)
		})
		.parse(metadata);
};
